import { getFileFromLog, getReasoningFromLog, Log } from "@/types/app/log";
import { Header } from "@components/SimpleTable/Headers";
import { JudgmentTags } from "@components/llama/Evaluations/EvaluationResult";
import { None } from "@components/llama/Tables/Logs/LogCells";
import React from "react";
import { EntityLabel } from "./EntityLabel";
import { EntityPopover } from "./shared/EntityPopover";
import { useAppHref } from "@/lib/path-utils";
import { ArrowsOutSimple } from "@phosphor-icons/react";
import IconButton from "@library/IconButton";

/**
 * This component is only the card. For the Label and Tooltip, use `FileLabelWithPopover`.
 */
interface Props {
  log: Log;
  target?: "_blank" | "_self";
  noRoot?: boolean; // If true, the root element will not be rendered. Used when this component isn't actually a popover, but the shared underlying content. E.g. FilePicker's Version preview.
  customAction?: () => void;
}

export const LogPopover = ({ log, target = "_blank", noRoot, customAction }: Props) => {
  const Root = noRoot ? React.Fragment : EntityPopover.Root;
  const href =
    useAppHref({
      id: getFileFromLog(log).id,
      page: "logs",
      type: "file",
    }) + `?id=${log.id}`;

  const reasoning = getReasoningFromLog(log);

  return (
    <Root>
      <EntityPopover.Header.Root>
        <EntityPopover.Header.Title>
          <EntityPopover.Header.Icon type={"log"} />
          <h3 className="text-12-12 font-bold">Log</h3>
          <EntityLabel hideIcon size={20} entity={"log"} id={log.id} interactive />
        </EntityPopover.Header.Title>

        {customAction ? (
          <div className="flex shrink-0 grow justify-end">
            <IconButton
              Icon={ArrowsOutSimple}
              shade="gray"
              className="border-none"
              styling="outline"
              size={24}
              onClick={customAction}
            />
          </div>
        ) : (
          <EntityPopover.Header.LinkButton tooltip={`View Log`} href={href} target={target} />
        )}
      </EntityPopover.Header.Root>

      <div className="flex flex-col gap-4 text-10-12">
        {"output" in log && log.output && (
          <>
            <Header>Output</Header>
            {/* TODO: this should have output_messages etc. */}
            <pre className="line-clamp-4 text-10-12">{log.output}</pre>
            <div className="h-8" />
          </>
        )}

        {"judgment" in log && log.judgment !== undefined && (
          <>
            <Header>Judgment</Header>
            <pre className="line-clamp-3 max-h-[60px] overflow-auto truncate whitespace-pre-wrap">
              <JudgmentTags evaluatorLog={log} evaluator={log.evaluator} />
            </pre>
            <div className="h-8" />
          </>
        )}
        {reasoning && (
          <>
            <Header>Reasoning</Header>
            <pre className="line-clamp-5 whitespace-pre-wrap">{reasoning || <None />}</pre>
            <div className="h-8" />
          </>
        )}

        {"stdout" in log && log.stdout !== undefined && (
          <>
            <Header>Stdout</Header>
            <pre className="line-clamp-5 whitespace-pre-wrap">{log?.stdout || <None />}</pre>
          </>
        )}
        <div className="h-8" />

        {"error" in log && log.error && (
          <>
            <Header>Error</Header>
            <pre className="line-clamp-5 whitespace-pre-wrap">{log?.error || <None />}</pre>
          </>
        )}
      </div>
      <EntityPopover.Author.Root>
        <EntityPopover.Author.Text>
          {log.user && <EntityPopover.Author.Name>{log.user}</EntityPopover.Author.Name>}
          <EntityPopover.Author.ActionAndTimestamp action="created" timestamp={log.created_at} />
        </EntityPopover.Author.Text>
      </EntityPopover.Author.Root>
    </Root>
  );
};
